import { type FC, useEffect, useMemo, useState } from 'react';
import {
  ArrowRightLeft,
  Copy,
  File,
  FileClock,
  History,
  ImagePlus,
  Info,
  type LucideIcon,
  Pen,
  Pencil,
  ToggleRight,
  Trash2,
} from 'lucide-react';
import { DateTime } from 'luxon';
import { type EventSuffix, Events, isShareAuditEvent } from '@/types/events';
import { useSelection } from '@/hooks/data/selection/useSelection';
import { CustomPagination } from '@/components/pagination';
import { auditDefaultPageSize } from '@/types/pagination';
import { useTreeContext } from '@/context/TreeContext';
import { useAudit } from '@/hooks/data/audit/useAudit';
import { usePagination } from '@/hooks/usePagination';
import { Skeleton } from '@/components/ui/skeleton';
import { type Audit } from '@/types/audit';
import { toNodeType } from '@/types/tree';
import { cn } from '@/lib/utils';
import { useTranslations } from 'next-intl';
import { useRouter } from 'next/router';

const suffixMeta: Record<EventSuffix, { Icon: LucideIcon; bg: string }> = {
  added: { Icon: ImagePlus, bg: 'bg-status-success' },
  activated: { Icon: ToggleRight, bg: 'bg-status-success' },
  renamed: { Icon: Pen, bg: 'bg-status-warning' },
  created: { Icon: File, bg: 'bg-status-success' },
  duplicated: { Icon: Copy, bg: 'bg-status-success' },
  updated: {
    Icon: Pencil,
    bg: 'bg-status-warning',
  },
  deleted: { Icon: Trash2, bg: 'bg-status-failure' },
  removed: { Icon: Trash2, bg: 'bg-status-failure' },
  trashed: { Icon: Trash2, bg: 'bg-status-failure' },
  moved: { Icon: ArrowRightLeft, bg: 'bg-status-progress' },
  restored: {
    Icon: History,
    bg: 'bg-status-success',
  },
  undeleted: {
    Icon: History,
    bg: 'bg-status-progress',
  },
  unknown: {
    Icon: Info,
    bg: 'bg-highlight-base',
  },
  reordered: {
    Icon: ArrowRightLeft,
    bg: 'bg-status-progress',
  },
};

export const AuditLogItem = ({ user, timestamp, event, details }: Audit) => {
  const suffix = (event.split('.').pop() as EventSuffix) ?? 'unknown';
  const { Icon, bg } = suffixMeta[suffix] || suffixMeta.unknown;

  const dateTime = DateTime.fromISO(timestamp);
  const formattedTimestamp = dateTime.toLocaleString(DateTime.DATETIME_FULL);

  // Audit will require a proper refactor but just leaving this for now to add the extra details. https://lkbks.atlassian.net/browse/DM-2492
  const collaborationText =
    isShareAuditEvent(event) && details && !Array.isArray(details) && 'collaborationEnabled' in details
      ? ` with collaboration ${details.collaborationEnabled ? 'enabled' : 'disabled'}`
      : '';

  const commentDetails =
    (event === 'comment.deleted' || event === 'comment.privacy.updated') &&
    details &&
    !Array.isArray(details) &&
    'body' in details
      ? (details as { body?: string; private?: boolean })
      : null;
  const privacyWord = event === 'comment.privacy.updated' ? ` ${commentDetails?.private ? 'private' : 'public'}` : '';
  const commentText = commentDetails ? `${privacyWord}: “${commentDetails.body ?? ''}”` : '';

  return (
    <div className="z-10 flex flex-row gap-x-4">
      <div className={cn('flex max-h-6 min-h-6 min-w-6 max-w-6 items-center justify-center rounded-full', bg)}>
        <Icon className="icon-3 stroke-text-inverted" strokeWidth={2.5} />
      </div>
      <div className="flex flex-col pt-px">
        <div className="leading-3">
          <span className="text-sm font-semibold break-all">{user?.name}</span>{' '}
          <span className="text-sm font-normal text-text-gray">
            {Events[event]}
            {collaborationText}
            {commentText}
          </span>
        </div>
        <div className="text-xs font-normal text-text-gray-alternate">{formattedTimestamp}</div>
      </div>
    </div>
  );
};

type AuditLogAccordionProps = {
  selectedItems: string | Array<{ id: string; name: string }>;
  entity?: string;
  enabled: boolean;
};

export const AuditLogAccordion: FC<AuditLogAccordionProps> = ({ selectedItems, entity = null, enabled }) => {
  const [page, setPage] = useState(1);
  const tAuditLog = useTranslations('inspector.auditLog');

  const { currentSelectedType } = useTreeContext();
  // The described node, not the tree's active node: with a child folder/album selected in the
  // gallery the Info tab above describes that child, so its audit trail must follow it too.
  const { describedNodeId, describedNodeType } = useSelection();
  const { query } = useRouter();

  // Compute both auditId and auditNodeType together to ensure they stay in sync
  const { auditId, auditNodeType } = useMemo<{ auditId: string | undefined; auditNodeType: string | undefined }>(() => {
    // Manage pages (selectedItems is a string ID)
    if (entity) {
      return { auditId: selectedItems as string, auditNodeType: entity };
    }

    // Node mode (selectedItems is always an array)
    const arr = selectedItems as Array<{ id: string; name: string }>;
    if (arr.length > 0) {
      return { auditId: arr[arr.length - 1].id, auditNodeType: currentSelectedType };
    }

    // Fallback to the described node (folder/album) when no assets are selected
    if (describedNodeId && describedNodeType) {
      // The audit API speaks NodeType; this node carries a DAMNodeType.
      return {
        auditId: describedNodeId,
        auditNodeType: toNodeType(describedNodeType),
      };
    }

    return { auditId: undefined, auditNodeType: undefined };
  }, [entity, selectedItems, describedNodeId, describedNodeType, currentSelectedType]);

  const { audits, auditsArePending, auditsAreFetching } = useAudit(page, auditDefaultPageSize, auditId, auditNodeType, {
    enabled: enabled && Boolean(auditId) && Boolean(auditNodeType),
  });

  useEffect(() => {
    //Reset audit page when changing asset selection
    setPage(1);
  }, [auditId]);

  const auditLogs = audits?.audit;

  const { offset, limit: pageSize = 0, count: totalCount = 0 } = audits?.pagination ?? {};

  const currentPage = (offset ?? 0) / (pageSize ?? 0) + 1;

  const paginationRange = usePagination({
    currentPage,
    totalCount,
    siblingCount: 1,
    pageSize,
  });

  const lastPage = paginationRange?.[paginationRange.length - 1];

  return (
    <div
      className={cn(
        'flex h-full flex-col overflow-y-auto overflow-x-hidden bg-ui-container p-3 scrollbar-none dark:focus-visible:bg-black/4 focus-visible:bg-white/4 focus-visible:outline-none',
        auditLogs?.length === 0 && !(auditsArePending || auditsAreFetching)
          ? 'items-center justify-center'
          : 'before:absolute before:inset-y-0 before:left-6 before:z-0 before:w-0.5 before:bg-ui-base before:content-[""]',
      )}
    >
      {auditsArePending ? (
        <div className="ml-px flex grow flex-col gap-y-5 pt-1">
          {Array.from({ length: 3 }).map((_, index) => (
            <div className="flex flex-row gap-x-2" key={`skeleton-${index}`}>
              <Skeleton className="max-h-6 min-h-6 w-full max-w-6 min-w-6 rounded-full" />
              <div className="flex w-full flex-col gap-y-1">
                <Skeleton className="h-6 w-11/12" />
                <Skeleton className="h-4 w-3/4" />
              </div>
            </div>
          ))}
        </div>
      ) : auditLogs && auditLogs.length > 0 ? (
        <>
          <div className="mb-5 ml-px flex grow flex-col gap-y-5 pt-1">
            {auditLogs?.map((log: Audit) => (
              <AuditLogItem key={log.id} {...log} />
            ))}
          </div>
          {audits?.pagination && audits.pagination.count > audits.pagination.limit && (
            <CustomPagination
              totalCount={totalCount}
              siblingCount={1}
              currentPage={currentPage}
              pageSize={pageSize}
              deselectAssetsOnPageChange={false}
              handleNext={{
                onClick() {
                  setPage((prev) => prev + 1);
                },
                href: {
                  query,
                },
                disabled: currentPage === lastPage,
              }}
              handlePrev={{
                onClick() {
                  setPage((prev) => prev - 1);
                },
                href: {
                  query,
                },
                disabled: Number(currentPage) === 1 || Number(currentPage) === 0,
              }}
              onPageChange={(selectedPage) => setPage(selectedPage ?? 1)}
              onlyIcons
            />
          )}
        </>
      ) : (
        <div className="py-8 text-center text-text-gray-alternate">
          <FileClock className="mx-auto mb-2" size={28} strokeWidth={1.5} />
          {tAuditLog('empty')}
        </div>
      )}
    </div>
  );
};
